import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The Web Speech API, which is prefixed everywhere that has it.
 *
 * Typed locally rather than pulled from lib.dom: the definitions are not in
 * every TypeScript version, and this is the whole surface we use.
 */
interface SpeechResultAlternative { transcript: string }
interface SpeechResult { 0: SpeechResultAlternative; isFinal: boolean; length: number }
interface SpeechResultList { length: number; [i: number]: SpeechResult }
interface SpeechEvent { resultIndex: number; results: SpeechResultList }
interface Recogniser {
  lang: string
  continuous: boolean
  interimResults: boolean
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((e: SpeechEvent) => void) | null
  onerror: (() => void) | null
  onend: (() => void) | null
}
type RecogniserCtor = new () => Recogniser

function ctor(): RecogniserCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: RecogniserCtor
    webkitSpeechRecognition?: RecogniserCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/** Is there a recogniser at all? Decided once, so the button can hide. */
export const voiceSupported = (): boolean => ctor() !== null

export interface Dictation {
  supported: boolean
  listening: boolean
  /** Start listening; every result so far is handed back as one string. */
  start: (locale: string, onText: (text: string) => void) => void
  stop: () => void
}

export function useDictation(): Dictation {
  const [listening, setListening] = useState(false)
  const rec = useRef<Recogniser | null>(null)

  // Never leave the microphone running because a sheet closed.
  useEffect(() => () => { rec.current?.abort() }, [])

  const stop = useCallback(() => {
    rec.current?.stop()
    setListening(false)
  }, [])

  const start = useCallback((locale: string, onText: (text: string) => void) => {
    const C = ctor()
    if (!C) return
    rec.current?.abort()

    const r = new C()
    // Roman Urdu has no speech model; Urdu's is the right one to listen with.
    r.lang = locale === 'ur-Latn' ? 'ur-PK' : locale
    r.continuous = false
    r.interimResults = true

    r.onresult = (e) => {
      let text = ''
      for (let i = 0; i < e.results.length; i++) {
        text += e.results[i]![0].transcript
      }
      onText(text.trim())
    }
    r.onerror = () => setListening(false)
    r.onend = () => setListening(false)

    rec.current = r
    setListening(true)
    r.start()
  }, [])

  return { supported: ctor() !== null, listening, start, stop }
}
